/* Linsoul brand wall: endless logo rows (GSAP, opposite directions, ease to a stop under the mouse or keyboard focus) + entrance. */
(() => {
  'use strict';
  const offscreen = (el) => {
    const r = el.getBoundingClientRect();
    return r.top > window.innerHeight || r.bottom < 0;
  };
  const loadGsap = (src) => (window.gsap ? Promise.resolve(window.gsap) : window.lsLoadGsap ? window.lsLoadGsap(src) : Promise.reject());

  function rows(root, gsap) {
    const speed = parseFloat(root.dataset.lbwSpeed) || 30;
    const list = [...root.querySelectorAll('[data-lbw-row]')].map((row) => ({ row, track: row.querySelector('[data-lbw-track]') })).filter((r) => r.track && r.track.children.length);
    if (!list.length) return () => {};
    let visible = false;

    const build = (r) => {
      if (r.tween) r.tween.kill();
      r.track.querySelectorAll('[data-lbw-clone]').forEach((c) => c.remove());
      gsap.set(r.track, { x: 0 });
      const items = [...r.track.children];
      const gap = parseFloat(getComputedStyle(r.track).columnGap) || 0;
      const setW = r.track.scrollWidth + gap - (parseFloat(getComputedStyle(r.track).paddingLeft) || 0) - (parseFloat(getComputedStyle(r.track).paddingRight) || 0);
      let total = setW;
      while (total < setW + r.row.clientWidth + 1) {
        items.forEach((item) => {
          const c = item.cloneNode(true);
          c.dataset.lbwClone = '';
          c.setAttribute('aria-hidden', 'true');
          c.querySelectorAll('a').forEach((a) => a.setAttribute('tabindex', '-1'));
          r.track.appendChild(c);
        });
        total += setW;
      }
      const duration = setW / speed;
      r.tween = r.row.dataset.dir === 'right'
        ? gsap.fromTo(r.track, { x: -setW }, { x: 0, duration, ease: 'none', repeat: -1 })
        : gsap.to(r.track, { x: -setW, duration, ease: 'none', repeat: -1 });
      r.tween.timeScale(r.hold ? 0 : 1);
      if (!visible) r.tween.pause();
    };

    const ease = (r, to) => {
      if (!r.tween) return;
      if (to > 0 && r.tween.paused()) r.tween.play();
      gsap.to(r.tween, { timeScale: to, duration: to ? 0.7 : 0.55, ease: to ? 'power2.in' : 'power2.out', overwrite: true });
    };

    list.forEach((r) => {
      r.row.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') { r.hold = true; ease(r, 0); } });
      r.row.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') { r.hold = false; ease(r, 1); } });
      r.row.addEventListener('focusin', () => { r.hold = true; ease(r, 0); });
      r.row.addEventListener('focusout', () => { r.hold = false; ease(r, 1); });
    });

    const io = new IntersectionObserver((entries) => {
      visible = entries[0].isIntersecting;
      list.forEach((r) => { if (!r.tween) return; if (visible) r.tween.play(); else r.tween.pause(); });
    });
    io.observe(root);

    let width = 0;
    const ro = new ResizeObserver(() => {
      if (root.clientWidth === width) return;
      width = root.clientWidth;
      list.forEach(build);
    });
    ro.observe(root);
    root.classList.add('is-moving');

    return () => {
      io.disconnect();
      ro.disconnect();
      list.forEach((r) => {
        if (r.tween) r.tween.kill();
        r.track.querySelectorAll('[data-lbw-clone]').forEach((c) => c.remove());
        gsap.set(r.track, { clearProps: 'transform' });
      });
      root.classList.remove('is-moving');
    };
  }

  function init(root) {
    if (root.dataset.lbwReady || !('IntersectionObserver' in window)) return;
    root.dataset.lbwReady = '1';
    const inEditor = !!(window.Shopify && Shopify.designMode);
    const near = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      near.disconnect();
      loadGsap(root.dataset.gsapSrc).then((gsap) => {
        gsap.matchMedia().add('(prefers-reduced-motion: no-preference)', () => {
          const stop = inEditor ? () => {} : rows(root, gsap);
          let tl;
          let enter;
          if (offscreen(root)) {
            const items = root.querySelectorAll('[data-lbw-reveal]');
            gsap.set(items, { autoAlpha: 0, y: 28 });
            tl = gsap.timeline({ paused: true, defaults: { ease: 'expo.out', duration: 0.9 } });
            tl.to(items, { autoAlpha: 1, y: 0, stagger: 0.06, clearProps: 'transform,opacity,visibility' });
            enter = new IntersectionObserver((entries) => {
              if (!entries.some((e) => e.isIntersecting)) return;
              enter.disconnect();
              tl.play();
            }, { rootMargin: '0px 0px -15% 0px' });
            enter.observe(root);
          }
          return () => { stop(); if (enter) enter.disconnect(); if (tl) tl.kill(); };
        });
      }).catch(() => {});
    }, { rootMargin: '600px 0px' });
    near.observe(root);
  }

  const run = () => document.querySelectorAll('[data-lbw]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
  document.addEventListener('shopify:section:load', run);
})();
